import { DayPilot } from 'daypilot-pro-react'
import { differenceInCalendarDays } from 'date-fns'
import { Capacitor } from '@capacitor/core'

import { BASE_DATE } from 'services/ScheduleTemplates'
import { getEventBubbleHtml, getTimeOffBubbleHtml, getAbsenceBubbleHtml } from 'services/schedule'
import { isMobile } from 'services/Utilities'
import { getHolidays } from 'services/Holidays'
import SchedulerHeader from 'Manager/Schedule/components/SchedulerHeader'
import { t, currentLang } from 'translations'

const locale = {
  fr: 'fr-fr',
  en: 'en-ca'
}

const config = (view, data) => {
  const { dispatch, ...options } = data
  let secondaryColor = '#eceeef'
  const localeLang = locale[currentLang()] || 'en-ca'
  // DAYPILOT bubble
  let bubble = new DayPilot.Bubble({
    animated: false,
    theme: 'bubble_bootstrap',
    cssOnly: true,
    showAfter: 1000,
    hideAfter: 1,
    onLoad: args => {
      if (args.source.data.isTimeOff) args.html = getTimeOffBubbleHtml(args.source.data)
      else if (args.source.data.isAbsence) args.html = getAbsenceBubbleHtml(args.source.data)
      else args.html = getEventBubbleHtml(args.source.data)
    }
  })

  // bubble, with async loading
  let resourceBubble = new DayPilot.Bubble({
    animated: false,
    theme: 'resource_bubble bubble_bootstrap',
    cssOnly: true,
    showAfter: 250,
    hideAfter: 1,
    onLoad: function (args) {
      args.html = args.source.name
    }
  })

  let defaultConfig = {
    days: 1,
    cellWidthSpec: 'Auto',
    cellWidthMin: '5',
    locale: localeLang,
    bubble,
    resourceBubble,
    cellDuration: '30',
    eventHeight: 23,
    cellSweeping: false,
    treeEnabled: true,
    treeIndent: 8,
    theme: 'merinio_scheduler',
    rowMarginBottom: 10,
    dynamicLoading: false,
    businessBeginsHour: 0,
    businessEndsHour: 24,
    dynamicEventRendering: 'Disabled',
    businessWeekends: true,
    eventResizingStartEndEnabled: true,
    eventMovingStartEndEnabled: true,
    timeRangeSelectingStartEndEnabled: true,
    eventResizingStartEndFormat: 'HH:mm',
    eventMovingStartEndFormat: 'HH:mm',
    timeRangeSelectingStartEndFormat: 'HH:mm',
    sortDirections: ['asc'],
    heightSpec: 'Max100Pct',
    crosshairType: 'Header',
    progressiveRowRendering: true,
    progressiveRowRenderingPreload: 10,
    timeHeaders: [{ groupBy: 'Day', format: 'dddd dd MMMM yyyy' }, { groupBy: 'Hour' }],
    onEventClick: null,
    eventClickHandling: 'Select',
    multiSelectRectangle: 'Free',
    eventDoubleClickHandling: 'Enabled',
    doubleClickTimeout: 250,
    allowMultiResize: true,
    allowMultiMove: true,
    treeAnimation: false
  }

  if (Capacitor.isNativePlatform() || isMobile()) {
    defaultConfig.cellDuration = '1440'
    defaultConfig.timeHeaders = [{ groupBy: 'Day', format: 'dddd dd MMMM yyyy' }]
    defaultConfig.eventResizingStartEndEnabled = true
    defaultConfig.eventMovingStartEndEnabled = true
  }

  const onBeforeTimeHeaderRenderWeekly = ({ header }) => {
    const holidaysInHeaderDay = getHolidays(header.start.value)
    if (holidaysInHeaderDay.length) {
      header.backColor = secondaryColor
    }
    header.cssClass = 'font-weight-bold'
  }

  const onBeforeTimeHeaderDomAddWeekly = args => {
    args.element = <SchedulerHeader notes={options.notes} header={args.header} view={view} localeLang={localeLang} dispatch={dispatch} />
  }

  const onBeforeTimeHeaderRenderMultiDay = args => {
    if (args.header.level === 0) {
      args.header.html = t('manager.scheduleTemplates.DAY_HEADER', {
        multiDayIndex: differenceInCalendarDays(new Date(args.header.start.value), BASE_DATE) + 1
      })
    }
  }

  const onBeforeTimeHeaderRenderDaily = args => {
    if (args.header.level === 0) {
      args.header.cssClass = 'font-weight-bold'
    }
  }

  const onBeforeTimeHeaderDomAddDaily = args => {
    args.element = <SchedulerHeader notes={options.notes} header={args.header} view={view} localeLang={localeLang} dispatch={dispatch} />
  }

  switch (view) {
    case 'weekly':
      return {
        ...defaultConfig,
        days: 7,
        eventHeight: 37,
        cellDuration: '1440',
        timeHeaders: [{ groupBy: 'Day', format: 'dddd d' }],
        onBeforeTimeHeaderRender: onBeforeTimeHeaderRenderWeekly,
        onBeforeTimeHeaderDomAdd: onBeforeTimeHeaderDomAddWeekly,
        ...options
      }
    case 'template':
      return { ...defaultConfig, timeHeaders: [{ groupBy: 'Hour' }], ...options, onBeforeTimeHeaderRender: () => null }
    case 'multiDayTemplate':
      return {
        ...defaultConfig,
        eventHeight: 37,
        timeHeaders: [{ groupBy: 'Day' }, { groupBy: 'Cell' }],
        cellDuration: '60',
        cellWidthMin: '40',

        onBeforeTimeHeaderRender: onBeforeTimeHeaderRenderMultiDay,
        ...options
      }
    case 'daily':
      return {
        ...defaultConfig,
        onBeforeTimeHeaderRender: onBeforeTimeHeaderRenderDaily,
        onBeforeTimeHeaderDomAdd: onBeforeTimeHeaderDomAddDaily,
        ...options
      }

    default:
      return { ...defaultConfig, ...options }
  }
}

export default config
